"use client";

import React from "react";
import { MapPin, Search, Building2, Users, Shield, ChevronRight, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const HIERARCHY_LEVELS = [{
  id: "mpn",
  abbr: "MPN",
  name: "Majelis Pimpinan Nasional",
  level: "Tingkat Nasional",
  color: "bg-[#B30000]",
  icon: Building2,
  desc: "Pimpinan tertinggi organisasi di tingkat nasional."
}, {
  id: "mpw",
  abbr: "MPW",
  name: "Majelis Pimpinan Wilayah",
  level: "Tingkat Provinsi",
  color: "bg-[#D4AF37]",
  icon: MapPin,
  desc: "Pimpinan organisasi di tingkat provinsi sebagai perpanjangan tangan MPN."
}, {
  id: "mpc",
  abbr: "MPC",
  name: "Majelis Pimpinan Cabang",
  level: "Tingkat Kabupaten / Kota",
  color: "bg-yellow-500",
  icon: Building2,
  desc: "Pimpinan cabang di tingkat kabupaten atau kota."
}, {
  id: "pac",
  abbr: "PAC",
  name: "Pimpinan Anak Cabang",
  level: "Tingkat Kecamatan",
  color: "bg-green-600",
  icon: Users,
  desc: "Pimpinan anak cabang di tingkat kecamatan."
}, {
  id: "ranting",
  abbr: "Ranting",
  name: "Pimpinan Ranting",
  level: "Tingkat Desa / Kelurahan",
  color: "bg-blue-600",
  icon: Users,
  desc: "Pimpinan ranting di tingkat desa atau kelurahan."
}, {
  id: "anak-ranting",
  abbr: "Anak Ranting",
  name: "Pimpinan Anak Ranting",
  level: "Tingkat RW / Dusun",
  color: "bg-purple-600",
  icon: Shield,
  desc: "Pimpinan anak ranting di tingkat RW, dusun atau lingkungan."
}];
function ChartContent() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  return <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Building2 className="size-5 text-primary" />
                HIERARKI NASIONAL
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {HIERARCHY_LEVELS.map((level, i) => <div key={level.id} data-index={i} className="flex gap-3 group">
                  <div className={`w-16 shrink-0 rounded-md text-white p-2 flex flex-col items-center justify-center text-center ${level.color}`}>
                    <level.icon className="size-5 mb-1 opacity-80" />
                    <span className="text-xs font-bold">{level.abbr}</span>
                  </div>
                  <div className="flex-1 pt-1">
                    <h4 className="text-sm font-bold leading-tight group-hover:text-primary transition-colors">{level.name}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">{level.level}</p>
                  </div>
                </div>)}
            </div>
            <div className="p-4 border-t bg-muted/10">
              <Button variant="outline" className="w-full text-xs h-8">
                Penjelasan Hierarki
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="lg:col-span-6 space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
            <h2 className="text-xl font-bold uppercase tracking-tight">Struktur Organisasi</h2>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">Bagan</Badge>
              <Badge variant="secondary">Daftar</Badge>
            </div>
          </div>

          {loading ? <div className="space-y-4">
              {[1, 2, 3].map((_, i) => <Card key={i} data-index={i} className="animate-pulse">
                  <CardHeader className="h-24 bg-muted/50 rounded-t-xl" />
                </Card>)}
            </div> : error ? <div className="p-8 text-center border rounded-xl bg-destructive/5 text-destructive">
              <p>Gagal memuat data struktur. Silakan coba lagi.</p>
            </div> : posts.length === 0 ? <div className="p-12 text-center border rounded-xl bg-muted/30">
              <Users className="size-12 mx-auto text-muted-foreground mb-4 opacity-20" />
              <p className="text-muted-foreground">Belum ada data profil struktur yang tersedia.</p>
            </div> : <div className="space-y-4">
              {posts.map((post, i) => {
            const status = post.customFields?.status || "Aktif";
            const isAktif = status.toLowerCase() === "aktif";
            return <Card key={post.id} data-index={i} className="overflow-hidden transition-all hover:shadow-md border-l-4" style={{
              borderLeftColor: isAktif ? 'var(--color-primary)' : 'var(--color-muted-foreground)'
            }}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <CardTitle className="text-lg leading-tight mb-1">{post.title}</CardTitle>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="secondary" className="font-normal rounded-sm px-1.5 py-0 h-5">
                              {post.customFields?.peran || "Pengurus"}
                            </Badge>
                            {post.customFields?.wilayah && <span className="flex items-center gap-1">
                                <MapPin className="size-3" />
                                {post.customFields.wilayah}
                              </span>}
                          </div>
                        </div>
                        <Badge variant={isAktif ? "default" : "secondary"} className={isAktif ? "bg-green-600 hover:bg-green-700 text-white" : "bg-muted text-muted-foreground"}>
                          {status}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    {post.customFields?.konten_struktur && <CardContent className="pb-4">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {post.customFields.konten_struktur}
                        </p>
                      </CardContent>}
                    
                    <CardFooter className="pt-0">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between text-primary hover:text-primary hover:bg-primary/5">
                        <Link to={post.link || "#"}>
                          Lihat Detail Profil
                          <ChevronRight className="size-4" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>;
          })}
            </div>}
        </div>

        {}
        <div className="lg:col-span-3 space-y-6">
          <Card className="sticky top-24">
            <CardHeader className="pb-4 border-b bg-muted/20">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="size-4 text-primary" />
                DIREKTORI WILAYAH
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase">Tingkat Organisasi</label>
                <Select defaultValue="mpc">
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Tingkat" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mpn">MPN (Nasional)</SelectItem>
                    <SelectItem value="mpw">MPW (Provinsi)</SelectItem>
                    <SelectItem value="mpc">MPC (Cabang)</SelectItem>
                    <SelectItem value="pac">PAC (Anak Cabang)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase">Provinsi</label>
                <Select defaultValue="jabar">
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Provinsi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jabar">Jawa Barat</SelectItem>
                    <SelectItem value="jateng">Jawa Tengah</SelectItem>
                    <SelectItem value="jatim">Jawa Timur</SelectItem>
                    <SelectItem value="dki">DKI Jakarta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase">Kabupaten / Kota</label>
                <Select defaultValue="garut">
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Kabupaten/Kota" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="garut">Kabupaten Garut</SelectItem>
                    <SelectItem value="bandung">Kota Bandung</SelectItem>
                    <SelectItem value="tasik">Kabupaten Tasikmalaya</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button className="w-full mt-2">
                Tampilkan Struktur
              </Button>
            </CardContent>
            
            <Separator />
            
            {}
            <div className="p-4 bg-muted/10">
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <h4 className="font-bold text-sm leading-tight">MPC PEMUDA PANCASILA KABUPATEN GARUT</h4>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] px-1.5 py-0">Aktif</Badge>
                </div>
                
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2 text-muted-foreground">
                    <Building2 className="size-3.5 shrink-0 mt-0.5" />
                    <div>
                      <span className="block font-medium text-foreground">Induk Organisasi</span>
                      MPW Pemuda Pancasila Jawa Barat
                    </div>
                  </div>
                  <div className="flex gap-2 text-muted-foreground">
                    <FileText className="size-3.5 shrink-0 mt-0.5" />
                    <div>
                      <span className="block font-medium text-foreground">Masa Bakti</span>
                      2023 - 2027
                    </div>
                  </div>
                </div>
                
                <Button variant="link" size="sm" className="w-full h-auto p-0 text-primary mt-2">
                  Lihat Struktur Lengkap
                </Button>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>;
}
export default function ZonaPPOrganizationalChart() {
  const wp_query_params = {
    post_type: "zona_pp_profile",
    per_page: 10,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="zonapporganizationalchart" className="bg-background w-full">
      {}
      <div className="bg-secondary text-secondary-foreground py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/cubes.webp')] mix-blend-overlay"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight">Struktur & Hierarki</h1>
            <div className="flex items-center gap-2 text-sm text-secondary-foreground/80 mb-6">
              <Link to="/PPZonePage" className="hover:text-white transition-colors">Beranda Zona PP</Link>
              <ChevronRight className="size-3" />
              <span className="text-white">Struktur & Hierarki</span>
            </div>
            <p className="text-secondary-foreground/90 text-lg max-w-2xl mb-8">
              Lihat susunan organisasi Pemuda Pancasila dari tingkat nasional sampai tingkat paling bawah berdasarkan data resmi dan terverifikasi.
            </p>
            <Button variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Search className="size-4 mr-2" />
              Cari Struktur Wilayah
            </Button>
          </div>
        </div>
      </div>

      {}
      <PostsProvider wp_query={{
      post_type: "zona_pp_profile",
      per_page: 10,
      orderby: "date",
      order: "desc"
    }}>
        <ChartContent />
      </PostsProvider>
    </section>;
}